/**
 * L'usage automatique de l'avoir : le réglage, et ce qu'il autorise.
 *
 * ## Pourquoi un verrou
 *
 * Au 16 septembre 2026, **56 familles portent une provision et une dette en
 * même temps**, pour 4 080,00 €. La reconstruction de l'historique bancaire
 * n'est pas finie : une partie de ces provisions sont peut-être des paiements
 * qui n'ont pas encore trouvé leur facture. Les dépenser d'office, c'est
 * réduire une facture récente avec l'argent d'une facture ancienne — et ne
 * s'en apercevoir qu'après.
 *
 * ## Ce qu'il ferme, et ce qu'il laisse ouvert
 *
 * Deux portes seulement : la déduction de l'avoir au panier, et la demande de
 * remboursement depuis l'espace parent. L'équipe garde tous ses gestes — notes
 * de crédit, placements, corrections, et l'enregistrement d'une demande reçue
 * par courriel une fois la famille vérifiée. Les montants restent visibles :
 * un avoir caché serait un avoir perdu pour tout le monde, à commencer par
 * ceux qui doivent le vérifier.
 *
 * ## Où la règle vit réellement
 *
 * **En base**, dans `v_user_provision_balances.available_cents`, que lisent
 * `quote_order` et `demander_remboursement`. Ce module ne décide rien : il
 * nomme les modes pour l'écran d'administration et explique le chiffre au
 * parent. Un écran qui se tromperait ne pourrait pas déverrouiller quoi que ce
 * soit.
 */

/** Les trois modes, du plus fermé au plus ouvert. */
export type ModeAvoirAuto = 'off' | 'familles_verifiees' | 'on';

/** L'état de vérification d'un compte familial. L'absence vaut `incomplet`. */
export type EtatDeVerification = 'incomplet' | 'en_cours' | 'verifie';

export const MODES: { mode: ModeAvoirAuto; titre: string; detail: string }[] = [
  {
    mode: 'off',
    titre: 'Bloqué pour toutes les familles',
    detail:
      'Aucun avoir n’est déduit d’un panier, aucune demande de remboursement n’est acceptée '
      + 'depuis l’espace parent. À garder tant que la reprise des mouvements bancaires est en cours.',
  },
  {
    mode: 'familles_verifiees',
    titre: 'Ouvert aux familles vérifiées',
    detail:
      'Seules les familles dont le compte a été attesté par l’équipe utilisent leur avoir. '
      + 'C’est le mode de la réouverture progressive.',
  },
  {
    mode: 'on',
    titre: 'Ouvert à toutes les familles',
    detail:
      'Plus aucune condition. À ne poser qu’une fois l’historique bancaire complet et rapproché.',
  },
];

/**
 * Le mode tel qu'il faut le lire, quoi qu'on ait trouvé en base.
 *
 * Une valeur inconnue, absente ou abîmée rend `off` : le défaut d'un verrou
 * est d'être fermé. La base applique exactement la même règle — ce n'est pas
 * une duplication de politique mais la même conclusion tirée deux fois, et
 * c'est celle qui ne peut pas laisser passer d'argent.
 */
export function versMode(brut: unknown): ModeAvoirAuto {
  return brut === 'on' || brut === 'familles_verifiees' ? brut : 'off';
}

/** L'état de vérification, replié sur `incomplet` si on ne reconnaît rien. */
export function versEtatDeVerification(brut: unknown): EtatDeVerification {
  return brut === 'verifie' || brut === 'en_cours' ? brut : 'incomplet';
}

/**
 * L'avoir de cette famille peut-il servir maintenant ?
 *
 * Sert à **expliquer** un chiffre, jamais à l'autoriser : la base a déjà
 * tranché en rendant `available_cents`.
 */
export function usageOuvert(mode: ModeAvoirAuto, etat: EtatDeVerification): boolean {
  if (mode === 'on') return true;
  if (mode === 'familles_verifiees') return etat === 'verifie';
  return false;
}
